// src/components/BurgerLadderPage.js
import React, { useState, useEffect } from "react";
import styles from "./BurgerLadderPage.module.css";
import teamLogos from "../utils/teamLogos";
import TEAM_INFO from "../utils/teamInfo";
import burgerIcon from "../components/images/burger_96.png";

// helper to get last Thursday (week boundary)
const getLastThursday = () => {
  const today = new Date();
  const day = today.getDay(); // 0 = Sun … 4 = Thu
  const diff = day >= 4 ? day - 4 : day + 3;
  const lastThu = new Date(today);
  lastThu.setDate(today.getDate() - diff);
  lastThu.setHours(0, 0, 0, 0);
  return lastThu;
};

const BurgerLadderPage = () => {
  const [currLadder, setCurrLadder] = useState([]);
  const [allLadder, setAllLadder] = useState([]);
  const [lastFiveStatus, setLastFiveStatus] = useState({});
  const [weeklyWinners, setWeeklyWinners] = useState([]);
  const [weeklyLosers, setWeeklyLosers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewAllTime, setViewAllTime] = useState(false);

  // map Squiggle team.id → our abbreviation
  const idToAbbrev = Object.fromEntries(
    Object.entries(TEAM_INFO).map(([abbr, info]) => [info.id, abbr])
  );

  useEffect(() => {
    setLoading(true);
    const startYear = 2023;
    const currentYear = new Date().getFullYear();

    // initialize ladder stats
    const initStats = () => {
      const stats = {};
      Object.values(TEAM_INFO).forEach(({ id }) => {
        stats[id] = { id, played: 0, burgerWins: 0 };
      });
      return stats;
    };

    // fetch standings for previous seasons
    const fetchStandings = (year, stats) =>
      fetch(`https://api.squiggle.com.au/?q=standings;year=${year}`)
        .then((res) => res.json())
        .then(({ standings }) => {
          standings.forEach(({ id, played, wins }) => {
            if (stats[id]) {
              stats[id].played += played;
              stats[id].burgerWins += wins;
            }
          });
        });

    // fetch completed games (for ladder and weekly)
    const fetchCompletedGames = (year, stats, collectWeekly = false) =>
      fetch(`https://api.squiggle.com.au/?q=games;year=${year};complete=100`)
        .then((res) => res.json())
        .then(({ games }) => {
          const winners = new Set();
          const allTeams = new Set();
          const weekStart = getLastThursday();

          games.forEach(({ hteamid, ateamid, winnerteamid, localtime }) => {
            if (stats[hteamid] && stats[ateamid]) {
              stats[hteamid].played++;
              stats[ateamid].played++;
              if (stats[winnerteamid]) stats[winnerteamid].burgerWins++;
            }
            if (collectWeekly && new Date(localtime) >= weekStart) {
              allTeams.add(hteamid);
              allTeams.add(ateamid);
              winners.add(winnerteamid);
            }
          });

          if (collectWeekly) {
            return {
              winners: Array.from(winners),
              losers: Array.from(allTeams).filter((id) => !winners.has(id)),
            };
          }
          return games;
        });

    // build this-season ladder + weekly
    const thisStats = initStats();
    const thisPromise = fetchCompletedGames(currentYear, thisStats, true).then(
      (weekly) => {
        setWeeklyWinners(weekly.winners);
        setWeeklyLosers(weekly.losers);
        return Object.values(thisStats).sort(
          (a, b) => b.burgerWins - a.burgerWins
        );
      }
    );

    // build all-time ladder
    const allStats = initStats();
    const years = Array.from(
      { length: currentYear - startYear + 1 },
      (_, i) => startYear + i
    ).filter((y) => y !== currentYear);

    const allPromise = Promise.all([
      ...years.map((y) => fetchStandings(y, allStats)),
      fetchCompletedGames(currentYear, allStats, false),
    ]).then(() =>
      Object.values(allStats).sort((a, b) => b.burgerWins - a.burgerWins)
    );

    // after ladders, fetch schedule + completed games for last five logic
    Promise.all([thisPromise, allPromise]).then(([thisArr, allArr]) => {
      setCurrLadder(thisArr);
      setAllLadder(allArr);

      Promise.all([
        fetch(`https://api.squiggle.com.au/?q=games;year=${currentYear}`),
        fetch(
          `https://api.squiggle.com.au/?q=games;year=${currentYear};complete=100`
        ),
      ])
        .then(async ([schedRes, compRes]) => {
          const { games: scheduleGames } = await schedRes.json();
          const { games: completeGames } = await compRes.json();

          // completed rounds only
          const completedRounds = Array.from(
            new Set(completeGames.map((g) => g.round))
          ).sort((a, b) => a - b);
          const maxRound = completedRounds[completedRounds.length - 1] || 0;

          // all scheduled rounds
          const allRounds = Array.from(
            new Set(scheduleGames.map((g) => g.round))
          ).sort((a, b) => a - b);

          // only up to last completed
          const eligibleRounds = allRounds.filter((r) => r <= maxRound);

          const map = {};
          Object.values(TEAM_INFO).forEach(({ id }) => {
            const statuses = [];
            for (
              let i = eligibleRounds.length - 1;
              i >= 0 && statuses.length < 5;
              i--
            ) {
              const r = eligibleRounds[i];
              const compGame = completeGames.find(
                (g) => g.round === r && (g.hteamid === id || g.ateamid === id)
              );
              if (compGame) {
                statuses.push(compGame.winnerteamid === id ? "win" : "loss");
              } else {
                const schedGame = scheduleGames.find(
                  (g) => g.round === r && (g.hteamid === id || g.ateamid === id)
                );
                if (schedGame) {
                  continue;
                }
                statuses.push("bye");
              }
            }
            while (statuses.length < 5) {
              statuses.unshift("bye");
            }
            map[id] = statuses.reverse();
          });

          setLastFiveStatus(map);
        })
        .finally(() => setLoading(false));
    });
  }, []);

  const display = viewAllTime ? allLadder : currLadder;

  return (
    <div className={styles.burgerLadderPage}>
      <h1 className={styles.title}>Burgers</h1>

      <div className={styles.recentContainer}>
        {weeklyWinners.length + weeklyLosers.length > 0 ? (
          <>
            <h1 className={styles.recentContainerHeader}>Completed Matches</h1>
            <div className={styles.recentRow}>
              {weeklyWinners.map((teamId) => {
                const abbr = idToAbbrev[teamId];
                const logoKey = TEAM_INFO[abbr].logoKey;
                return (
                  <img
                    key={teamId}
                    src={teamLogos[logoKey]}
                    alt={`${abbr} won burger`}
                    className={styles.recentLogo}
                    style={{ opacity: 1 }}
                  />
                );
              })}
            </div>

            <div className={styles.recentRow}>
              {weeklyLosers.map((teamId) => {
                const abbr = idToAbbrev[teamId];
                const logoKey = TEAM_INFO[abbr].logoKey;
                return (
                  <img
                    key={teamId}
                    src={teamLogos[logoKey]}
                    alt={`${abbr} lost burger`}
                    className={styles.recentLogo}
                    style={{ opacity: 0.25 }}
                  />
                );
              })}
            </div>
          </>
        ) : (
          <p className={styles.noWinnersText}>No Round Winners Yet.</p>
        )}
      </div>

      <div className={styles.toggleButtons}>
        <button
          className={!viewAllTime ? styles.active : ""}
          onClick={() => setViewAllTime(false)}
        >
          This Season
        </button>

        <button
          className={viewAllTime ? styles.active : ""}
          onClick={() => setViewAllTime(true)}
        >
          All Seasons
        </button>
      </div>

      <div className={styles.content}>
        {loading ? (
          <p className={styles.loadingText}>Loading…</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th></th>
                <th>Team</th>
                <th>Last 5</th>
                <th>P</th>
                <th>
                  <img
                    src={burgerIcon}
                    alt="Burger wins"
                    className={styles.headerIcon}
                  />
                </th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {display.map((team, idx) => {
                const abbr = idToAbbrev[team.id];
                const logoKey = TEAM_INFO[abbr].logoKey;
                const statuses = lastFiveStatus[team.id] || [];
                return (
                  <tr key={team.id}>
                    <td>{idx + 1}</td>
                    <td className={styles.logoCell}>
                      <img
                        src={teamLogos[logoKey]}
                        alt={`${abbr} logo`}
                        className={styles.teamLogo}
                      />
                    </td>
                    <td className={styles.teamNameCell}>{abbr}</td>
                    <td className={styles.lastGamesCell}>
                      {statuses.map((s, i) =>
                        s === "win" ? (
                          <img
                            key={i}
                            src={burgerIcon}
                            alt="Win"
                            className={styles.lastGameIcon}
                          />
                        ) : s === "loss" ? (
                          <span key={i} className={styles.lossIcon}>
                            ✖
                          </span>
                        ) : (
                          <span key={i} className={styles.byeIcon} />
                        )
                      )}
                    </td>
                    <td>{team.played}</td>
                    <td>{team.burgerWins}</td>
                    <td>
                      {team.played
                        ? ((team.burgerWins / team.played) * 100).toFixed(1)
                        : "0.0"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BurgerLadderPage;
